import {
  Add,
  AccountBalance,
  Apartment,
  Gavel,
  LocationCity,
  Search,
  Settings,
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  useAdministrativeUnits,
  useAuthorities,
  useMunicipalities,
  useAdminUnitMunicipalities,
  useCourtAdministrativeUnitAssignments,
  useCourtRoles,
  useCourts,
  useFederalStates,
  usePostCodes,
  useCourtMunicipalityAssignments,
  useCreateCourtMunicipalityAssignment,
  useDeleteCourtMunicipalityAssignment,
  useCourtPostcodeAssignments,
  useCreateCourtPostcodeAssignment,
  useDeleteCourtPostcodeAssignment,
  useAssignPostcodeMunicipality,
} from '../../Aemter/hooks';
import { AddAuthorityDialog } from '../../Aemter/Authorities/AddAuthorityDialog';
import { AddMunicipalityDialog } from '../../Aemter/Municipalities/AddMunicipalityDialog';
import { LoadingSpinner } from '../../core/LoadingSpinner';
import { AddAdminUnitDialog } from '../../Aemter/AdminUnits/AddAdminUnitDialog';
import { AddCourtDialog } from '../../Aemter/Courts/AddCourtDialog';
import { CourtAssignmentDetailPane } from './CourtAssignmentDetailPane';
import { GemarkungDetailPane } from './GemarkungDetailPane';
import { MunicipalityDetailPane } from './MunicipalityDetailPane';
import {
  AnchorOfficeAssignmentPane,
  type InheritedOffice,
} from './AnchorOfficeAssignmentPane';
import type { AemterNavTarget } from './aemterNav';
import { PostcodeSearch } from '../../Clients/AddressTab/PostcodeSearch';
import { CourtRolesManager } from './CourtRolesManager';
import type { IFederalState } from '../../Interfaces/IAemter';
import type { ICourt } from '../../Interfaces/ICourt';
import type { IPostcode } from '../../Interfaces/IPostcode';
import type { IMunicipality } from '../../Interfaces/IMunicipality';

// Wrapper, der `usePostCodes` erst beim Öffnen des Dialogs aktiviert. So
// blockiert das Postcodes-Endpoint (langsam, riesig) nicht den initialen
// Render der Ämter-Workbench. Beim Öffnen erscheint kurz ein Spinner; erst
// wenn die Postleitzahlen-Liste da ist, wird der eigentliche Dialog gerendert.
const AddCourtDialogLazy: React.FC<{
  courts: ICourt[];
  federalStates: IFederalState[];
  onClose: () => void;
}> = ({ courts, federalStates, onClose }) => {
  const { postcodes, isLoadingPostcodes } = usePostCodes();
  if (isLoadingPostcodes) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0,0,0,0.2)',
          zIndex: 1300,
        }}
      >
        <LoadingSpinner />
      </Box>
    );
  }
  return (
    <AddCourtDialog
      courts={courts}
      federalStates={federalStates}
      postcodes={postcodes ?? []}
      onClose={onClose}
    />
  );
};

type Mode = 'gemarkung' | 'municipality' | 'postcode' | 'court' | 'authority';

/**
 * Zentrale Ämter-Workbench: ein einheitlicher Ort, um Gemarkungen, Gerichte
 * und ihre Rollen-Zuordnungen zu pflegen. Die alte 3-Spalten-Kaskade
 * (Bundesland → Gericht → Gemarkung) ist durch zwei flexible Modi ersetzt:
 *
 *   • „Gemarkungen": Gemarkung wählen → für die Gemarkung alle Gerichte
 *     mit ihren Rollen sehen und bearbeiten (Standard-Workflow).
 *   • „Gerichte": Gericht wählen → sehen, in welchen Gemarkungen es welche
 *     Rolle innehat.
 *
 * Stammdaten-Pflege (Bundesländer/Gerichtsrollen) ist über Toolbar-Buttons
 * erreichbar — dadurch bleiben sie verfügbar, drängen sich aber nicht in den
 * primären Pfad.
 */
export const CourtOverview: React.FC = () => {
  const { courts, isLoadingCourts, refetchCourts } = useCourts();
  const { authorities, isLoadingAuthorities, refetchAuthorities } =
    useAuthorities();
  const { municipalities, isLoadingMunicipalities, refetchMunicipalities } =
    useMunicipalities();
  const {
    adminUnitMunicipalities,
    isLoadingAdminUnitMunicipalities,
    refetchAdminUnitMunicipalities,
  } = useAdminUnitMunicipalities();
  const { courtRoles, isLoadingCourtRoles, refetchCourtRoles } =
    useCourtRoles();
  const { federalStates, isLoadingFederalStates } = useFederalStates();
  const {
    administrativeUnits,
    isLoadingAdministrativeUnits,
    refetchAdministrativeUnits,
  } = useAdministrativeUnits();
  const {
    courtAdministrativeUnitAssignments,
    isLoadingCourtAdministrativeUnitAssignments,
    refetchCourtAdministrativeUnitAssignments,
  } = useCourtAdministrativeUnitAssignments();
  const {
    courtMunicipalityAssignments,
    isLoadingCourtMunicipalityAssignments,
    refetchCourtMunicipalityAssignments,
  } = useCourtMunicipalityAssignments();
  const {
    courtPostcodeAssignments,
    isLoadingCourtPostcodeAssignments,
    refetchCourtPostcodeAssignments,
  } = useCourtPostcodeAssignments();
  const { createCourtMunicipalityAssignment } =
    useCreateCourtMunicipalityAssignment();
  const { deleteCourtMunicipalityAssignment } =
    useDeleteCourtMunicipalityAssignment();
  const { createCourtPostcodeAssignment } = useCreateCourtPostcodeAssignment();
  const { deleteCourtPostcodeAssignment } = useDeleteCourtPostcodeAssignment();
  const { assignPostcodeMunicipality } = useAssignPostcodeMunicipality();

  // Für den PLZ-Reiter: die gewählte PLZ wird per Suche bestimmt (67k Zeilen ->
  // keine Master-Liste). Objekt hält idPostcode + Anzeige + Bundesland.
  const [selectedPostcode, setSelectedPostcode] = useState<IPostcode | null>(
    null
  );
  // PLZ↔Gemeinde ändern (aus dem PLZ-Reiter).
  const [editingPlzMun, setEditingPlzMun] = useState(false);
  const [plzMunChoice, setPlzMunChoice] = useState<IMunicipality | null>(null);

  const [mode, setMode] = useState<Mode>('gemarkung');
  const [federalStateFilter, setFederalStateFilter] = useState<number | 'all'>(
    'all'
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showAddUnit, setShowAddUnit] = useState(false);
  const [showAddCourt, setShowAddCourt] = useState(false);
  const [showAddAuthority, setShowAddAuthority] = useState(false);
  const [showAddMunicipality, setShowAddMunicipality] = useState(false);
  const [showRolesManager, setShowRolesManager] = useState(false);

  // ALLE Hooks unbedingt vor frühen Returns — sonst „Rendered more hooks
  // than during the previous render" beim Übergang Loading → Loaded.
  // Postcodes werden hier NICHT mehr abgewartet: das `/postcode`-Endpoint ist
  // riesig und langsam; der AddCourtDialog braucht sie sowieso erst beim
  // Öffnen. Wir laden sie lazy im Dialog selber.
  // Zuordnungs-Zähler EINMALIG als Map vorberechnen. Vorher lief pro Listenzeile
  // ein `.filter().length` über die Mappings — bei ~11.000 Gemeinden × mehreren
  // tausend Zuordnungen sind das zweistellige Millionen Operationen bei JEDEM
  // Tastendruck (die useMemo hängt am searchTerm). Mit den Maps ist es O(n+m).
  const countMaps = useMemo(() => {
    const unit = new Map<number, number>();
    const court = new Map<number, number>();
    const authority = new Map<number, number>();
    const muni = new Map<number, number>();
    const inc = (map: Map<number, number>, key?: number | null) => {
      if (key === undefined || key === null) return;
      map.set(key, (map.get(key) ?? 0) + 1);
    };
    (courtAdministrativeUnitAssignments ?? []).forEach(a => {
      inc(unit, a.idAdministrativeUnit);
      inc(court, a.idCourt);
      inc(authority, a.idAuthority);
    });
    // Gemeinde-/PLZ-Zuordnungen mitzählen — sonst zeigt der Zähler bei
    // Behörden (z. B. Finanzämtern, die keine Gemarkung haben) fälschlich 0.
    (courtMunicipalityAssignments ?? []).forEach(a => {
      if (a.activeAssignment !== false) {
        inc(court, a.idCourt);
        inc(authority, a.idAuthority);
      }
    });
    (courtPostcodeAssignments ?? []).forEach(a => {
      if (a.activeAssignment !== false) {
        inc(court, a.idCourt);
        inc(authority, a.idAuthority);
      }
    });
    (adminUnitMunicipalities ?? []).forEach(x => {
      if (x.activeAssignment !== false) inc(muni, x.idMunicipality);
    });
    return { unit, court, authority, muni };
  }, [
    courtAdministrativeUnitAssignments,
    courtMunicipalityAssignments,
    courtPostcodeAssignments,
    adminUnitMunicipalities,
  ]);

  const visibleItems = useMemo(() => {
    type Item = {
      id: number;
      primary: string;
      secondary: string;
      count: number;
    };
    if (
      !administrativeUnits ||
      !courts ||
      !authorities ||
      !municipalities ||
      !adminUnitMunicipalities ||
      !federalStates ||
      !courtAdministrativeUnitAssignments
    ) {
      return [] as Item[];
    }
    const fsName = (idFederalState?: number) =>
      federalStates.find(f => f.idFederalState === idFederalState)
        ?.FederalState ?? '';
    const inFilter = (idFederalState?: number) =>
      federalStateFilter === 'all' || idFederalState === federalStateFilter;
    const term = searchTerm.trim().toLowerCase();

    if (mode === 'gemarkung') {
      return administrativeUnits
        .filter(u => inFilter(u.idFederalState))
        .filter(u =>
          term ? u.AdministrativeUnit.toLowerCase().includes(term) : true
        )
        .sort((a, b) =>
          a.AdministrativeUnit.localeCompare(b.AdministrativeUnit)
        )
        .map(u => ({
          id: u.idAdministrativeUnit,
          primary: u.AdministrativeUnit,
          secondary: fsName(u.idFederalState),
          count: countMaps.unit.get(u.idAdministrativeUnit) ?? 0,
        }));
    }
    if (mode === 'court') {
      return courts
        .filter(c => inFilter(c.idFederalState))
        .filter(c => (term ? c.Court.toLowerCase().includes(term) : true))
        .sort((a, b) => a.Court.localeCompare(b.Court))
        .map(c => ({
          id: c.idCourt,
          primary: c.Court,
          secondary: fsName(c.idFederalState),
          count: countMaps.court.get(c.idCourt) ?? 0,
        }));
    }
    if (mode === 'authority') {
      return authorities
        .filter(a => inFilter(a.idFederalState))
        .filter(a => (term ? a.Authority.toLowerCase().includes(term) : true))
        .sort((a, b) => a.Authority.localeCompare(b.Authority))
        .map(a => ({
          id: a.idAuthority,
          primary: a.Authority,
          secondary: fsName(a.idFederalState),
          count: countMaps.authority.get(a.idAuthority) ?? 0,
        }));
    }
    if (mode === 'municipality') {
      return municipalities
        .filter(m => inFilter(m.idFederalState))
        .filter(m =>
          term ? m.Municipality.toLowerCase().includes(term) : true
        )
        .sort((a, b) => a.Municipality.localeCompare(b.Municipality))
        .map(m => ({
          id: m.idMunicipality,
          primary: m.Municipality,
          secondary: fsName(m.idFederalState),
          count: countMaps.muni.get(m.idMunicipality) ?? 0,
        }));
    }
    return [] as Item[];
  }, [
    mode,
    federalStateFilter,
    searchTerm,
    administrativeUnits,
    courts,
    authorities,
    municipalities,
    adminUnitMunicipalities,
    federalStates,
    courtAdministrativeUnitAssignments,
    countMaps,
  ]);

  // Renderliste stückweise laden: eine ungefilterte Gemeinde-Liste hätte ~11.000
  // DOM-Zeilen und würde die Ansicht einfrieren. Wir zeigen zunächst ein Batch und
  // bieten „Weitere laden" an. Bei Moduswechsel/Suche/Filter zurücksetzen.
  const VISIBLE_STEP = 500;
  const [visibleLimit, setVisibleLimit] = useState(VISIBLE_STEP);
  useEffect(() => {
    setVisibleLimit(VISIBLE_STEP);
  }, [mode, searchTerm, federalStateFilter]);
  const shownItems = visibleItems.slice(0, visibleLimit);
  const hiddenCount = visibleItems.length - shownItems.length;

  if (
    isLoadingCourts ||
    isLoadingAuthorities ||
    isLoadingMunicipalities ||
    isLoadingAdminUnitMunicipalities ||
    isLoadingCourtRoles ||
    isLoadingFederalStates ||
    isLoadingAdministrativeUnits ||
    isLoadingCourtAdministrativeUnitAssignments ||
    isLoadingCourtMunicipalityAssignments ||
    isLoadingCourtPostcodeAssignments
  ) {
    return <LoadingSpinner height='80vh' />;
  }
  if (
    !courts ||
    !authorities ||
    !municipalities ||
    !adminUnitMunicipalities ||
    !courtRoles ||
    !federalStates ||
    !administrativeUnits ||
    !courtAdministrativeUnitAssignments ||
    !courtMunicipalityAssignments ||
    !courtPostcodeAssignments
  ) {
    return (
      <Alert severity='error'>Die Daten konnten nicht geladen werden!</Alert>
    );
  }

  const handleModeChange = (newMode: Mode) => {
    setMode(newMode);
    setSelectedId(null);
  };

  const onAnyChange = () => {
    refetchCourts();
    refetchAuthorities();
    refetchMunicipalities();
    refetchAdminUnitMunicipalities();
    refetchAdministrativeUnits();
    refetchCourtAdministrativeUnitAssignments();
    refetchCourtMunicipalityAssignments();
    refetchCourtPostcodeAssignments();
  };

  // Gemarkung trägt nur noch das Grundbuchamt — die Rollenauswahl der
  // Gemarkungs-/Gerichts-Zuordnung wird darauf beschränkt.
  const grundbuchamtRoles = (courtRoles ?? []).filter(
    r => r.CourtRole === 'Grundbuchamt'
  );
  const grundbuchamtRoleId = grundbuchamtRoles[0]?.idCourtRole;

  // Zentrale Navigation: zu einem referenzierten Element springen. Gemarkung/
  // Gemeinde/Gericht/Behörde über die Liste, PLZ über das Suchobjekt.
  const navigateTo = (target: AemterNavTarget) => {
    switch (target.kind) {
      case 'gemarkung':
        setMode('gemarkung');
        setSelectedId(target.id);
        break;
      case 'municipality':
        setMode('municipality');
        setSelectedId(target.id);
        break;
      case 'court':
        setMode('court');
        setSelectedId(target.id);
        break;
      case 'authority':
        setMode('authority');
        setSelectedId(target.id);
        break;
      case 'postcode':
        setMode('postcode');
        setSelectedPostcode(target.postcode);
        break;
    }
  };

  // PLZ↔Gemeinde ändern (eine PLZ gehört zu genau einer Gemeinde).
  const changePlzMunicipality = async () => {
    if (!selectedPostcode?.idPostcode || !plzMunChoice) return;
    try {
      await assignPostcodeMunicipality({
        idPostcode: selectedPostcode.idPostcode,
        idMunicipality: plzMunChoice.idMunicipality,
      });
      setSelectedPostcode({
        ...selectedPostcode,
        idMunicipality: plzMunChoice.idMunicipality,
        Municipality: plzMunChoice.Municipality,
        AGS: plzMunChoice.AGS ?? null,
      });
      setEditingPlzMun(false);
      setPlzMunChoice(null);
      onAnyChange();
    } catch (e) {
      console.error(e);
    }
  };

  // Gemeinde-Ansicht: die über die zugeordneten Gemarkungen SUMMIERTEN
  // Grundbuchämter (read-only — bearbeitet werden sie an der Gemarkung).
  const inheritedGrundbuchaemter = (
    idMunicipality: number
  ): InheritedOffice[] => {
    const gemarkungIds = new Set(
      (adminUnitMunicipalities ?? [])
        .filter(
          x =>
            x.idMunicipality === idMunicipality && x.activeAssignment !== false
        )
        .map(x => x.idAdministrativeUnit)
    );
    const byCourt = new Map<number, number>();
    (courtAdministrativeUnitAssignments ?? []).forEach(a => {
      if (
        a.activeAssignment !== false &&
        a.idCourt &&
        a.idCourtRole === grundbuchamtRoleId &&
        gemarkungIds.has(a.idAdministrativeUnit)
      ) {
        byCourt.set(a.idCourt, (byCourt.get(a.idCourt) ?? 0) + 1);
      }
    });
    return Array.from(byCourt.entries()).map(([idCourt, count]) => ({
      id: `gb-${idCourt}`,
      roleLabel: 'Grundbuchamt',
      officeLabel:
        (courts ?? []).find(c => c.idCourt === idCourt)?.Court ??
        `Gericht ${idCourt}`,
      note: `über ${count} Gemarkung${count > 1 ? 'en' : ''}`,
      navTarget: { kind: 'court', id: idCourt },
    }));
  };

  // PLZ-Ansicht: die Gemarkungen der zugeordneten Gemeinde (read-only, navigierbar).
  const gemarkungenOfMunicipality = (
    idMunicipality: number | null | undefined
  ): { key: string; label: string; navTarget: AemterNavTarget }[] => {
    if (idMunicipality === null || idMunicipality === undefined) return [];
    const ids = new Set(
      (adminUnitMunicipalities ?? [])
        .filter(
          x =>
            x.idMunicipality === idMunicipality && x.activeAssignment !== false
        )
        .map(x => x.idAdministrativeUnit)
    );
    return (administrativeUnits ?? [])
      .filter(u => ids.has(u.idAdministrativeUnit))
      .sort((a, b) => a.AdministrativeUnit.localeCompare(b.AdministrativeUnit))
      .map(u => ({
        key: `g-${u.idAdministrativeUnit}`,
        label: u.AdministrativeUnit,
        navTarget: { kind: 'gemarkung', id: u.idAdministrativeUnit },
      }));
  };

  // PLZ-Ansicht: die über die Gemeinde geerbten Gerichte/Behörden (read-only),
  // markiert, wenn eine PLZ-direkte Zuweisung dieselbe Rolle überschreibt.
  const inheritedFromMunicipality = (
    idMunicipality: number | null | undefined,
    idPostcode: number
  ): InheritedOffice[] => {
    if (idMunicipality === null || idMunicipality === undefined) return [];
    const plzRoleIds = new Set(
      (courtPostcodeAssignments ?? [])
        .filter(
          a => a.idPostcode === idPostcode && a.activeAssignment !== false
        )
        .map(a => a.idCourtRole)
    );
    return (courtMunicipalityAssignments ?? [])
      .filter(
        a => a.idMunicipality === idMunicipality && a.activeAssignment !== false
      )
      .map(a => ({
        id: `mun-${a.idAssignment}`,
        roleLabel: a.CourtRole ?? `Rolle ${a.idCourtRole}`,
        officeLabel: a.Court ?? a.Authority ?? '',
        note: 'über Gemeinde',
        overridden: plzRoleIds.has(a.idCourtRole),
        navTarget: a.idAuthority
          ? { kind: 'authority', id: a.idAuthority }
          : a.idCourt
            ? { kind: 'court', id: a.idCourt }
            : undefined,
      }));
  };

  return (
    <Box>
      {/* === Toolbar === */}
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
      >
        <Box>
          <Typography variant='h5'>Ämterverwaltung</Typography>
          <Typography variant='caption' color='text.secondary'>
            Zuständige Gerichte und Behörden für Gemarkungen, Gemeinden und PLZ
            pflegen.
          </Typography>
        </Box>
        <Stack direction='row' spacing={1}>
          <Button
            variant='outlined'
            startIcon={<Settings />}
            onClick={() => setShowRolesManager(true)}
          >
            Rollen pflegen
          </Button>
        </Stack>
      </Stack>

      <Alert severity='info' variant='outlined' sx={{ mb: 2 }}>
        Die zuständigen Gerichte und Behörden einer PLZ leiten sich
        grundsätzlich über die zugeordnete <strong>Gemeinde</strong> ab.
        Unterscheidet sich der Gerichtsbezirk oder die Zuständigkeit für eine
        einzelne PLZ (z.&nbsp;B. Randbezirke), kann die PLZ direkt einem Gericht
        oder einer Behörde zugeordnet werden — diese Zuweisung überschreibt dann
        je Rolle die von der Gemeinde geerbte. Das <strong>Grundbuchamt</strong>{' '}
        wird über die <strong>Gemarkung</strong> geführt.
      </Alert>

      {/* === Filter-Leiste === */}
      <Paper variant='outlined' sx={{ p: 1.5, mb: 2 }}>
        <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
          <ToggleButtonGroup
            value={mode}
            exclusive
            size='small'
            onChange={(_, v) => v && handleModeChange(v as Mode)}
          >
            <ToggleButton value='gemarkung'>
              <Apartment fontSize='small' sx={{ mr: 0.5 }} />
              Gemarkung
            </ToggleButton>
            <ToggleButton value='municipality'>
              <LocationCity fontSize='small' sx={{ mr: 0.5 }} />
              Gemeinde
            </ToggleButton>
            <ToggleButton value='postcode'>
              <Search fontSize='small' sx={{ mr: 0.5 }} />
              PLZ
            </ToggleButton>
            <ToggleButton value='court'>
              <Gavel fontSize='small' sx={{ mr: 0.5 }} />
              Gerichte
            </ToggleButton>
            <ToggleButton value='authority'>
              <AccountBalance fontSize='small' sx={{ mr: 0.5 }} />
              Behörden
            </ToggleButton>
          </ToggleButtonGroup>

          {mode === 'postcode' ? (
            <Typography variant='body2' color='text.secondary' sx={{ flex: 1 }}>
              PLZ rechts über die Suche wählen, um die zuständigen Gerichte zu
              pflegen.
            </Typography>
          ) : (
            <>
              <TextField
                select
                size='small'
                label='Bundesland'
                value={federalStateFilter}
                onChange={e =>
                  setFederalStateFilter(
                    e.target.value === 'all' ? 'all' : Number(e.target.value)
                  )
                }
                sx={{ minWidth: 200 }}
              >
                <MenuItem value='all'>Alle Bundesländer</MenuItem>
                {federalStates
                  .slice()
                  .sort((a, b) => a.FederalState.localeCompare(b.FederalState))
                  .map(fs => (
                    <MenuItem key={fs.idFederalState} value={fs.idFederalState}>
                      {fs.FederalState}
                    </MenuItem>
                  ))}
              </TextField>

              <TextField
                size='small'
                placeholder={
                  mode === 'gemarkung'
                    ? 'Gemarkung suchen…'
                    : mode === 'court'
                      ? 'Gericht suchen…'
                      : mode === 'authority'
                        ? 'Behörde suchen…'
                        : 'Gemeinde suchen…'
                }
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                sx={{ flex: 1, minWidth: 240 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Search fontSize='small' />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                variant='contained'
                startIcon={<Add />}
                onClick={() =>
                  mode === 'gemarkung'
                    ? setShowAddUnit(true)
                    : mode === 'court'
                      ? setShowAddCourt(true)
                      : mode === 'authority'
                        ? setShowAddAuthority(true)
                        : setShowAddMunicipality(true)
                }
              >
                {mode === 'gemarkung'
                  ? 'Neue Gemarkung'
                  : mode === 'court'
                    ? 'Neues Gericht'
                    : mode === 'authority'
                      ? 'Neue Behörde'
                      : 'Neue Gemeinde'}
              </Button>
            </>
          )}
        </Stack>
      </Paper>

      {/* === Workbench === */}
      {
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Paper
            variant='outlined'
            sx={{
              width: 320,
              flexShrink: 0,
              maxHeight: 'calc(100vh - 280px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {mode === 'postcode' ? (
              <Box sx={{ p: 2 }}>
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                  Postleitzahl wählen
                </Typography>
                <PostcodeSearch
                  postcodeObject={selectedPostcode}
                  updatePostcodeObject={p => setSelectedPostcode(p ?? null)}
                  required={false}
                />
                {selectedPostcode && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ mt: 1, display: 'block' }}
                  >
                    Gewählt: {selectedPostcode.Postcode} {selectedPostcode.City}
                    {selectedPostcode.Municipality
                      ? ` · ${selectedPostcode.Municipality}`
                      : ''}
                  </Typography>
                )}
              </Box>
            ) : visibleItems.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant='body2' color='text.secondary'>
                  Keine Einträge gefunden.
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
                {shownItems.map(item => (
                  <ListItem key={item.id} disablePadding divider>
                    <ListItemButton
                      selected={selectedId === item.id}
                      onClick={() => setSelectedId(item.id)}
                    >
                      <ListItemText
                        primary={item.primary}
                        secondary={item.secondary}
                      />
                      <Chip
                        label={item.count}
                        size='small'
                        color={item.count > 0 ? 'primary' : 'default'}
                        variant={item.count > 0 ? 'filled' : 'outlined'}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
                {hiddenCount > 0 && (
                  <ListItem
                    divider
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      gap: 1,
                    }}
                  >
                    <ListItemText
                      primaryTypographyProps={{
                        variant: 'caption',
                        color: 'text.secondary',
                      }}
                      primary={`… und ${hiddenCount} weitere. Für eine genauere Auswahl die Suche verfeinern.`}
                    />
                    <Button
                      size='small'
                      variant='outlined'
                      onClick={() => setVisibleLimit(l => l + VISIBLE_STEP)}
                    >
                      Weitere {Math.min(hiddenCount, VISIBLE_STEP)} laden
                    </Button>
                  </ListItem>
                )}
              </List>
            )}
          </Paper>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {mode === 'postcode' ? (
              selectedPostcode?.idPostcode ? (
                <AnchorOfficeAssignmentPane
                  anchorKind='postcode'
                  anchorName={`${selectedPostcode.Postcode} ${selectedPostcode.City}`}
                  anchorFederalState={null}
                  courts={courts}
                  authorities={authorities}
                  courtRoles={courtRoles}
                  allowedCategories={['court', 'authority']}
                  assignments={courtPostcodeAssignments.filter(
                    a =>
                      a.idPostcode === selectedPostcode.idPostcode &&
                      a.activeAssignment !== false
                  )}
                  onCreate={payload =>
                    createCourtPostcodeAssignment({
                      idAssignment: -1,
                      idPostcode: selectedPostcode.idPostcode!,
                      activeAssignment: true,
                      ...payload,
                    })
                  }
                  onDelete={idAssignment =>
                    deleteCourtPostcodeAssignment({
                      idAssignment,
                      idPostcode: selectedPostcode.idPostcode!,
                      idCourtRole: 0,
                    })
                  }
                  onAnyChange={onAnyChange}
                  contextLine={`Zugeordnete Gemeinde: ${
                    selectedPostcode.Municipality ?? '—'
                  }`}
                  contextNavTarget={
                    selectedPostcode.idMunicipality
                      ? {
                          kind: 'municipality',
                          id: selectedPostcode.idMunicipality,
                        }
                      : undefined
                  }
                  contextEditor={
                    !editingPlzMun ? (
                      <Button
                        size='small'
                        onClick={() => {
                          setEditingPlzMun(true);
                          setPlzMunChoice(null);
                        }}
                      >
                        Gemeinde ändern
                      </Button>
                    ) : (
                      <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{ flexWrap: 'wrap' }}
                      >
                        <Autocomplete
                          size='small'
                          sx={{ minWidth: 240 }}
                          openOnFocus
                          options={municipalities}
                          value={plzMunChoice}
                          onChange={(_, v) => setPlzMunChoice(v)}
                          getOptionLabel={o =>
                            o.AGS
                              ? `${o.Municipality} (AGS ${o.AGS})`
                              : o.Municipality
                          }
                          isOptionEqualToValue={(o, v) =>
                            o.idMunicipality === v.idMunicipality
                          }
                          filterOptions={(opts, state) => {
                            const q = state.inputValue.trim().toLowerCase();
                            if (!q) return opts.slice(0, 50);
                            return opts
                              .filter(o =>
                                o.Municipality.toLowerCase().includes(q)
                              )
                              .slice(0, 50);
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              label='Neue Gemeinde'
                              placeholder='Gemeinde suchen…'
                            />
                          )}
                        />
                        <Button
                          size='small'
                          variant='contained'
                          onClick={() => void changePlzMunicipality()}
                          disabled={!plzMunChoice}
                        >
                          Übernehmen
                        </Button>
                        <Button
                          size='small'
                          onClick={() => {
                            setEditingPlzMun(false);
                            setPlzMunChoice(null);
                          }}
                        >
                          Abbrechen
                        </Button>
                      </Stack>
                    )
                  }
                  contextChipsTitle='Gemarkungen (über die Gemeinde)'
                  contextChips={gemarkungenOfMunicipality(
                    selectedPostcode.idMunicipality
                  )}
                  inheritedTitle='Über die Gemeinde abgeleitet'
                  inherited={inheritedFromMunicipality(
                    selectedPostcode.idMunicipality,
                    selectedPostcode.idPostcode
                  )}
                  onNavigate={navigateTo}
                />
              ) : (
                <Paper variant='outlined' sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant='body2' color='text.secondary'>
                    Wählen Sie links eine Postleitzahl, um die zuständigen
                    Gerichte zu pflegen. Eine PLZ-Zuweisung überschreibt je
                    Rolle die über die Gemeinde geerbte Stelle.
                  </Typography>
                </Paper>
              )
            ) : selectedId !== null ? (
              mode === 'municipality' ? (
                municipalities.find(m => m.idMunicipality === selectedId) ? (
                  <MunicipalityDetailPane
                    municipality={
                      municipalities.find(m => m.idMunicipality === selectedId)!
                    }
                    federalStates={federalStates}
                    administrativeUnits={administrativeUnits}
                    mappings={adminUnitMunicipalities}
                    onAnyChange={onAnyChange}
                    onNavigate={navigateTo}
                    officesSlot={
                      <AnchorOfficeAssignmentPane
                        anchorKind='municipality'
                        anchorName={
                          municipalities.find(
                            m => m.idMunicipality === selectedId
                          )?.Municipality ?? ''
                        }
                        anchorFederalState={
                          municipalities.find(
                            m => m.idMunicipality === selectedId
                          )?.idFederalState ?? null
                        }
                        courts={courts}
                        authorities={authorities}
                        courtRoles={courtRoles}
                        allowedCategories={['court', 'authority']}
                        assignments={courtMunicipalityAssignments.filter(
                          a =>
                            a.idMunicipality === selectedId &&
                            a.activeAssignment !== false
                        )}
                        onCreate={payload =>
                          createCourtMunicipalityAssignment({
                            idAssignment: -1,
                            idMunicipality: selectedId,
                            activeAssignment: true,
                            ...payload,
                          })
                        }
                        onDelete={idAssignment =>
                          deleteCourtMunicipalityAssignment({
                            idAssignment,
                            idMunicipality: selectedId,
                            idCourtRole: 0,
                          })
                        }
                        onAnyChange={onAnyChange}
                        inheritedTitle='Grundbuchamt (über die zugeordneten Gemarkungen)'
                        inherited={inheritedGrundbuchaemter(selectedId)}
                        onNavigate={navigateTo}
                      />
                    }
                  />
                ) : null
              ) : mode === 'gemarkung' ? (
                administrativeUnits.find(
                  u => u.idAdministrativeUnit === selectedId
                ) ? (
                  <GemarkungDetailPane
                    adminUnit={
                      administrativeUnits.find(
                        u => u.idAdministrativeUnit === selectedId
                      )!
                    }
                    federalStates={federalStates}
                    courts={courts}
                    courtRoles={courtRoles}
                    assignments={courtAdministrativeUnitAssignments}
                    municipalities={municipalities}
                    adminUnitMunicipalities={adminUnitMunicipalities}
                    onAnyChange={onAnyChange}
                    onNavigate={navigateTo}
                  />
                ) : null
              ) : (
                <CourtAssignmentDetailPane
                  mode={mode}
                  entityId={selectedId}
                  federalStates={federalStates}
                  courts={courts}
                  authorities={authorities}
                  administrativeUnits={administrativeUnits}
                  courtRoles={courtRoles}
                  municipalities={municipalities}
                  assignments={courtAdministrativeUnitAssignments}
                  courtMunicipalityAssignments={courtMunicipalityAssignments}
                  courtPostcodeAssignments={courtPostcodeAssignments}
                  onNavigate={navigateTo}
                  onAnyChange={onAnyChange}
                  onDeleted={() => setSelectedId(null)}
                />
              )
            ) : (
              <Paper variant='outlined' sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant='body2' color='text.secondary'>
                  {mode === 'gemarkung'
                    ? 'Wählen Sie eine Gemarkung aus der Liste, um das zuständige Grundbuchamt zu pflegen.'
                    : mode === 'court'
                      ? 'Wählen Sie ein Gericht aus der Liste, um seine Grundbuchamts-Gemarkungen zu pflegen.'
                      : mode === 'authority'
                        ? 'Wählen Sie eine Behörde aus der Liste. Behörden werden je Gemeinde im Reiter „Gemeinde" zugeordnet.'
                        : 'Wählen Sie eine Gemeinde aus der Liste, um Gemarkungen sowie zuständige Gerichte/Behörden zu pflegen.'}
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>
      }

      {/* === Dialogs === */}
      {showAddUnit && (
        <AddAdminUnitDialog
          onClose={() => setShowAddUnit(false)}
          currentAdminUnits={administrativeUnits}
          federalStates={federalStates}
        />
      )}
      {showAddCourt && (
        <AddCourtDialogLazy
          courts={courts}
          federalStates={federalStates}
          onClose={() => setShowAddCourt(false)}
        />
      )}
      {showAddAuthority && (
        <AddAuthorityDialog
          authorities={authorities}
          federalStates={federalStates}
          onClose={() => setShowAddAuthority(false)}
          onCreated={onAnyChange}
        />
      )}
      {showAddMunicipality && (
        <AddMunicipalityDialog
          municipalities={municipalities}
          federalStates={federalStates}
          onClose={() => setShowAddMunicipality(false)}
          onCreated={onAnyChange}
        />
      )}
      {showRolesManager && (
        <CourtRolesManager
          onClose={() => setShowRolesManager(false)}
          onChange={() => refetchCourtRoles()}
        />
      )}
    </Box>
  );
};
